// src/components/HomeFeatures/HomeFeatures.jsx
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import TagManager from 'react-gtm-module';
import styles from './HomeFeatures.module.scss';
import { imageMapping } from '../../assets/images/HomeFeatures/imageMapping';

const MotionDiv = motion.div;
const MotionLink = motion(Link);

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' },
  },
};

const imageHover = {
  scale: 1.05,
  transition: { type: 'tween', duration: 0.5, ease: 'easeOut' },
};

const buttonHover = {
  scale: 1.1,
  y: -5,
  boxShadow: '0px 4px 15px rgba(0, 0, 0, 0.2)',
  backgroundColor: 'var(--tertiary-hover)',
  color: 'var(--text-color)',
};

const buttonTransition = {
  type: 'tween',
  ease: 'easeOut',
  duration: 0.2,
};

const FeatureItem = ({ feature }) => {
  const controls = useAnimation();
  const [ref, inView] = useInView({ threshold: 0.3, triggerOnce: true });
  const navigate = useNavigate();

  useEffect(() => {
    if (inView) controls.start('visible');
  }, [controls, inView]);

  const handleClick = () => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'ux_event',
        event_name: 'home_feature_link',
        slug: feature.linea,
      },
    });
    navigate(`/productos/${feature.linea}`);
  };

  const imageSrc = imageMapping[feature.image];
  const isLink = Boolean(feature.linea);
  const Container = isLink ? MotionLink : MotionDiv;
  const containerProps = isLink
    ? { to: `/productos/${feature.linea}`, onClick: handleClick }
    : {};

  return (
    <Container
      ref={ref}
      className={styles.feature}
      variants={itemVariants}
      initial="hidden"
      animate={controls}
      {...containerProps}
    >
      <MotionDiv
        className={styles.feature__image}
        variants={itemVariants}
        whileHover={imageHover}
      >
        <img src={imageSrc} alt={feature.title} />
      </MotionDiv>

      <MotionDiv className={styles.feature__content} variants={itemVariants}>
        <div className={styles.feature__text}>
          <h3>{feature.title}</h3>
          <p>{feature.description}</p>
        </div>

        {feature.linea && (
          <MotionDiv
            className={styles.feature__button}
            whileHover={buttonHover}
            transition={buttonTransition}
          >
            Ver más
          </MotionDiv>
        )}
      </MotionDiv>
    </Container>
  );
};

FeatureItem.propTypes = {
  feature: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
    icono: PropTypes.string.isRequired,
    linea: PropTypes.string,
  }).isRequired,
};

const HomeFeatures = ({ data }) => (
  <section className={styles.features}>
    {data.map((item) => (
      <FeatureItem feature={item} key={item.id} />
    ))}
  </section>
);

HomeFeatures.propTypes = {
  data: PropTypes.arrayOf(FeatureItem.propTypes.feature).isRequired,
};

export default HomeFeatures;
